import { useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import useDocumentTitle from "@/components/ui/common/document-title";
import { useGetDashboardQuery } from "@/services/organizationApi";
import { Button } from "@/components/ui/button";
import { ExternalLink, ArrowLeft } from "lucide-react";
const { VITE_METABASE_URL } = import.meta.env;

const Dashboard = () => {
  const { dashboardId } = useParams<{ dashboardId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const period = searchParams.get("period");

  useDocumentTitle(dashboardId ? `Dashboard #${dashboardId}` : "Dashboard");
  const { data: dashboardInfo } = useGetDashboardQuery(
    dashboardId || period
      ? { dashboard_id: dashboardId, period: period || undefined }
      : undefined
  );

  const dashboardManagementAccess = (() => {
    try {
      const accessLevels = JSON.parse(localStorage.getItem("accessLevels") || "{}");
      return accessLevels.hide_dashbaord_management;
    } catch {
      return undefined;
    }
  })();

  const canManageDashboard = dashboardManagementAccess === "write" || dashboardManagementAccess === "full_access";

  const iframeSrc = useMemo(() => {
    if (!dashboardInfo?.dashboard_url) return "";
    let url = dashboardInfo.dashboard_url;
    if (period) {
      if (url.includes("#")) {
        url += `&period=${encodeURIComponent(period)}&hide_parameters=period`;
      } else {
        url += `#period=${encodeURIComponent(period)}&hide_parameters=period`;
      }
    }
    return url;
  }, [dashboardInfo?.dashboard_url, period]);

  return (
    <div className="grid gap-6 py-6 px-3 2xl:!max-w-[1400px] mx-auto ">
      <div className="flex items-center justify-between">
        {dashboardId ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/dashboard")}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Overview
          </Button>
        ) : (
          <div />
        )}

        {canManageDashboard && (
          <Button
            type="button"
            onClick={() => window.open(VITE_METABASE_URL, "_blank", "noopener,noreferrer")}
            className="flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Manage Dashboard
          </Button>
        )}
      </div>

      <iframe 
        className="h-screen w-full" 
        src={iframeSrc}
        title={dashboardId ? `Metabase Dashboard ${dashboardId}` : "Metabase Dashboard"}
      />
    </div>
  );
};

export default Dashboard;
